// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { getCurrentCountries } from "@insite/client-framework/Services/WebsiteService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import {
    extractGoogleAddressDetails,
    getZipCodeAddressCookie,
    saveZipCodeAddress,
} from "@west/App/Handlers/ZipCode/ZipCodeHelper";
import { getGeoCodeFromAddress, getGeoCodeFromLatLng } from "@west/App/Hooks/GoogleMaps/getGeoCodeFromAddress";
import { getGeoLocateViaIp } from "@west/App/Services/GoogleApiService";
import { GeoLocation, getGeoLocation } from "@west/XDL/Utilities/location";

type HandlerType = ApiHandlerDiscreteParameter<
    HasOnSuccess & HasOnError<string>,
    string,
    google.maps.GeocoderResult[] | undefined,
    { currentZipCode?: string; currentLocation?: GeoLocation }
>;

export const BeginLoadZipCode: HandlerType = props => {
    const cookie = getZipCodeAddressCookie();

    // If cookie exists, and zipcode is set...
    if (cookie && cookie.zipCode && cookie.zipCode.length > 0) {
        // Skip the handler chain if Country is set
        if (cookie.country && cookie.country.length > 0) {
            const result = {
                zipCode: cookie.zipCode,
                country: cookie.country,
                error: "",
            };
            props.dispatch({ type: "xHeader/update", data: { key: "zipCodeAddress", value: result } } as any);
            return false;
        }

        // Otherwise, use our cookie zipcode (to lookup country)
        props.currentZipCode = cookie.zipCode;
    }
};

// Get location by client side (Browser)
export const GetLocationByGeoLocationBrowser: HandlerType = async props => {
    // Continue if we have a saved ZipCode
    if (props.currentZipCode) {
        return;
    }

    // Otherwise, try and get from browser
    try {
        const result = await getGeoLocation({ lat: 0, lng: 0 });
        if (result.status === "success") {
            props.currentLocation = {
                lat: result.lat,
                lng: result.lng,
                status: "success",
            };
        }
    } catch (e) {
        // Do nothing. Continue without browser location
    }
};

// Get location by GeoLocation IP (Google)
export const GetLocationByIP: HandlerType = async props => {
    // Continue if we have a saved ZipCode OR browser location already set
    if (props.currentZipCode || props.currentLocation?.status === "success") {
        return;
    }

    // Otherwise, try and lookup via IP address
    try {
        const googleMapsApiKey = getSettingsCollection(props.getState()).websiteSettings.googleMapsApiKey;
        const result = await getGeoLocateViaIp(googleMapsApiKey);
        const { lat, lng } = result.location;

        if (lat !== 0 && lng !== 0) {
            props.currentLocation = {
                lat,
                lng,
                status: "success",
            };
        }
    } catch (e) {
        // Do nothing. Continue without IP location
    }
};

export const GetGoogleLocation: HandlerType = props => {
    return new Promise<false | void>((resolve, _) => {
        if (props.currentZipCode) {
            getGeoCodeFromAddress(props.currentZipCode)
                .then(r => {
                    props.apiResult = r;
                    resolve();
                })
                .catch(e => {
                    props.apiResult = e === google.maps.GeocoderStatus.ZERO_RESULTS ? [] : undefined;
                    resolve();
                });
        } else if (props.currentLocation?.status === "success") {
            getGeoCodeFromLatLng(props.currentLocation.lat, props.currentLocation.lng)
                .then(r => {
                    props.apiResult = r;
                    resolve();
                })
                .catch(e => {
                    props.apiResult = e === google.maps.GeocoderStatus.ZERO_RESULTS ? [] : undefined;
                    resolve();
                });
        } else {
            props.apiResult = undefined;
            resolve();
        }
    });
};

export const GetZipCode: HandlerType = async props => {
    const countryList = await getCurrentCountries({});
    const { error, ...result } = extractGoogleAddressDetails(props.apiResult, countryList);

    if (error) {
        const website = props.getState()?.context?.website;

        if (!website) {
            markSkipOnCompleteIfOnErrorIsSet(props);
            props.parameter.onError?.(error);
            return false;
        }

        let country = website.companyCountry;
        if (countryList?.countries) {
            const foundCountry = countryList.countries.find(c => c.name === country);
            if (foundCountry?.abbreviation) {
                country = foundCountry.abbreviation;
            }
        }

        result.zipCode = props.currentZipCode || website.postalCode;
        result.country = country;
    }

    saveZipCodeAddress(props.dispatch, result);
};

/**
 * We loadCart to tell BE about a new value
 * @param props
 * @constructor
 */
export const LoadCart: HandlerType = async props => {
    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
    await awaitableLoadCurrentCart({ forceRecalculation: true })(props.dispatch, props.getState);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    BeginLoadZipCode,
    GetLocationByGeoLocationBrowser,
    GetLocationByIP,
    GetGoogleLocation,
    GetZipCode,
    LoadCart,
    ExecuteOnSuccessCallback,
];

const loadZipCode = createHandlerChainRunner(chain, "loadZipCode");
export default loadZipCode;
